import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';

import { themeZafire } from '@zafire/ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { ZfBox, ZfIcon, ZfLink, ZfStack, ZfTypography } from '@zafire/react';
import { format, parse, isValid } from 'date-fns';
import { ColDef, ICellRendererParams, ValueFormatterParams } from 'ag-grid-enterprise';
import { ClientRepRow } from 'types/LegalPartner';
import { useGetBRPartnerDetails } from 'hooks/useGetBRPartnersDetails';
import { useGetBRLinkedClientRepresentatives } from 'hooks/useGetBRLinkedClientRepresentatives';
import ContactNote from 'types/ContactNote';
import { GlobalState } from 'reducers/stateTypes';
import { EnvConfig } from 'config/envConfig';

const BRClientRepresentativeTableView = () => {
  const { techId } = useParams<{ techId: string }>();
  const { t } = useTranslation();
  const contactNote: ContactNote | null = useSelector((state: GlobalState) => state.contactNoteList.shownNote);
  const { data: brPartnerDetails, isFetching: brPartnerDetailsLoading } = useGetBRPartnerDetails(
    contactNote?.relatedPartners.map((item) => item.partnerUId) || [],
    true,
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      sortable: true,
    }),
    [],
  );

  const { data: linkedClientRepDetails, isFetching: linkedClientRepDetailsLoading } =
    useGetBRLinkedClientRepresentatives(techId || '', true);

  const columnDefs = useMemo<ColDef<ClientRepRow>[]>(() => {
    return [
      {
        field: 'bizKey',
        headerName: 'Partner ID',
        cellRenderer: (params: ICellRendererParams) => {
          return (
            <ZfLink
              size="small"
              type="inline"
              target="_blank"
              rel="noreferrer"
              key={params.data.uuid}
              href={`${EnvConfig.config.hostUrl}/partner/edit/${params.data.uuid}`}
            >
              {params.data.bizKey}
            </ZfLink>
          );
        },
      },
      { field: 'name', headerName: 'Name', flex: 1 },
      {
        field: 'dateOfOrigin',
        headerName: t('contactNote.dialog.brClientRepresentative.dobHeader'),
        flex: 1,
        valueFormatter: (params: ValueFormatterParams<ClientRepRow>) => {
          if (params.value) {
            const parsedDate = parse(params.value, 'yyyyMMdd', new Date());
            if (isValid(parsedDate)) {
              return format(parsedDate, 'dd.MM.yyyy');
            }
          }
          return '-';
        },
      },
      {
        field: 'type',
        headerName: t('contactNote.dialog.brClientRepresentative.type'),
        filter: 'agSetColumnFilter',
        flex: 1,
        filterParams: {
          valueFormatter: (params: any) => {
            if (params.value) {
              const label = params.value.toUpperCase();
              const displayLabel = t(`contactNote.dialog.brClientRepresentative.types.${label}`, label) as string;
              return displayLabel.charAt(0) + displayLabel.slice(1)?.toLowerCase();
            }
            return '-';
          },
        },
        cellRenderer: (params: ICellRendererParams<ClientRepRow>) => {
          if (params.value) {
            const label = params.value.toUpperCase();
            return (
              <ZfStack direction="row" style={{ alignItems: 'center', height: '100%' }}>
                <ZfTypography variant="body-small" transform="capitalize">
                  {String(t(`contactNote.dialog.brClientRepresentative.types.${label}`, label)).toLowerCase()}
                </ZfTypography>
              </ZfStack>
            );
          }
          return '-';
        },
      },
      {
        field: 'linked',
        filter: 'agSetColumnFilter',
        headerName: 'Linked to BR',
        flex: 1,
        cellRenderer: (params: ICellRendererParams<ClientRepRow>) => {
          if (params.value === t('contactNote.dialog.brClientRepresentative.yes')) {
            return (
              <ZfStack direction="row" spacing="medium" style={{ alignItems: 'center', height: '100%' }}>
                {' '}
                <ZfIcon name="check" size="medium" />{' '}
                <ZfTypography variant="body-small">
                  {t('contactNote.dialog.brClientRepresentative.yes')}
                </ZfTypography>{' '}
              </ZfStack>
            );
          } else {
            return (
              <ZfStack direction="row" spacing="medium" style={{ alignItems: 'center', height: '100%' }}>
                {' '}
                <ZfIcon name="close" size="medium" />{' '}
                <ZfTypography variant="body-small">
                  {t('contactNote.dialog.brClientRepresentative.no')}
                </ZfTypography>{' '}
              </ZfStack>
            );
          }
        },
      },
    ];
  }, [contactNote?.relatedPartners]);

  const linkedClientRepList = useMemo(() => {
    if (brPartnerDetails && brPartnerDetails.length > 0) {
      if (!!linkedClientRepDetails && linkedClientRepDetails.length > 0) {
        return brPartnerDetails.map((partner) => {
          const isLinked = linkedClientRepDetails.some((rep) => rep.uuid === partner.uuid);
          return {
            ...partner,
            linked: isLinked
              ? t('contactNote.dialog.brClientRepresentative.yes')
              : t('contactNote.dialog.brClientRepresentative.no'),
          };
        });
      } else {
        return brPartnerDetails.map((partner) => ({
          ...partner,
          linked: t('contactNote.dialog.brClientRepresentative.no'),
        }));
      }
    }
    return [];
  }, [linkedClientRepDetails, brPartnerDetails]);

  return (
    <ZfBox styles={{ maxHeight: '500px', overflowY: 'auto' }}>
      <ZfBox styles={{ width: '100%' }}>
        <AgGridReact<any>
          domLayout="autoHeight"
          loading={brPartnerDetailsLoading || linkedClientRepDetailsLoading}
          getRowId={(params) => `${params.data.uuid}`}
          columnDefs={columnDefs}
          theme={themeZafire}
          pagination={true}
          paginationPageSize={10}
          paginationPageSizeSelector={[10, 20, 50]}
          rowData={linkedClientRepList}
          suppressCellFocus
          rowSelection={{ mode: 'singleRow', checkboxes: false }}
          defaultColDef={defaultColDef}
        />
      </ZfBox>
    </ZfBox>
  );
};

export default BRClientRepresentativeTableView;
